import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TenantsPropertyDto } from 'src/app/main/core/models/sites.model';
import { GetUserPermissionsForEditOutput, UpdateUserPermissionsInput } from 'src/app/_shared/core/models/permissions.model';
import { environment } from 'src/environments/environment';
import { ChangePasswordDto, PasswordEditDto } from '../../../account/core/models/passwords.model';
import { GetCameraPermissionDto, GrantCameraPermissionDto } from '../../../_shared/core/models/camera-permissions.model';
import { CreateCameraPermissionsDto, CreateOrEditPropertyGroupDto, CreateOrEditPropertyGroupSitesDto, CreateOrEditPropertyGroupUserDto, PropertyGroupsResult, PropertyGroupUserDto } from '../models/groups.model';
import { UserSiteListModel } from '../models/notification.model';
import { CreateOrUpdateRoleInput, GetRoleForEditOutput, IPagedRoleModelData, RolesFilterModel } from '../models/roles.model';
import { CreateOrUpdateUserInput, EntityDtoOfInt64, GetUserForEditOutput, PagedResultDtoOfUserListDto, PasswordComplexityResponseModelData, UserDto, UserListFilter } from '../models/user.model';

const API_BASE_URL = environment.apiUrl;

@Injectable({
  providedIn: 'root'
})
export class AdminHttpService {

  constructor(private http: HttpClient) { }

  getAllUsers(input: UserListFilter): Observable<PagedResultDtoOfUserListDto> {

    let url = API_BASE_URL + "api/User/GetUsers?tenantId=" + input.tenantId;
    if (input.filter) {
      url = url + "&filter=" + encodeURIComponent(input.filter);
    }
    if (input.sorting) {
      url = url + "&sorting=" + input.sorting;
    }
    if (input.skipCount) {
      url = url + "&skipCount=" + input.skipCount;
    }
    if (input.maxResultCount) {
      url = url + "&maxResultCount=" + input.maxResultCount;
    }
    if (input.isOnlyNormalUsers) {
      url = url + "&isOnlyNormalUsers=" + input.isOnlyNormalUsers;
    }
    url = url.replace(/[?&]$/, "");
    return this.http.get<PagedResultDtoOfUserListDto>(url);
  }

  getAllDropDownUsers(): Observable<PagedResultDtoOfUserListDto> {
    let url = API_BASE_URL + "api/User/GetDropdownUsers";
    url = url.replace(/[?&]$/, "");
    return this.http.get<PagedResultDtoOfUserListDto>(url);
  }

  getUserForEdit(id: number): Observable<GetUserForEditOutput> {
    let url = API_BASE_URL + "api/services/app/User/GetUserForEdit?";
    if (id !== undefined)
      url += "Id=" + encodeURIComponent("" + id) + "&";
    url = url.replace(/[?&]$/, "");
    return this.http.get<GetUserForEditOutput>(url);
  }

  createUser(input: CreateOrUpdateUserInput): Observable<void> {
    const notFoundError = new Error('Failed to create user.');

    let url = API_BASE_URL + "api/User";
    url = url.replace(/[?&]$/, "");

    return this.http.post<CreateOrUpdateUserInput>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  updateUser(input: CreateOrUpdateUserInput): Observable<void> {
    const notFoundError = new Error('Failed to update user.');

    let url = API_BASE_URL + "api/User";
    url = url.replace(/[?&]$/, "");

    return this.http.put<CreateOrUpdateUserInput>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  lockUser(id: EntityDtoOfInt64): Observable<void> {
    const notFoundError = new Error('Not Found');

    let url = API_BASE_URL + "api/services/app/User/LockUser";
    url = url.replace(/[?&]$/, "");

    return this.http.post<EntityDtoOfInt64>(url, id).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  unlockUser(id: EntityDtoOfInt64): Observable<void> {
    const notFoundError = new Error('Not Found');

    let url = API_BASE_URL + "api/services/app/User/UnlockUser";
    url = url.replace(/[?&]$/, "");

    return this.http.post<EntityDtoOfInt64>(url, id).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  deleteUser(id: number): Observable<void> {
    const notFoundError = new Error('Failed to delete user.');

    let url = API_BASE_URL + "api/services/app/User/DeleteUser?";
    if (id !== undefined)
      url += "Id=" + encodeURIComponent("" + id) + "&";
    url = url.replace(/[?&]$/, "");

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.http.delete<void>(url, options_).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  getPasswordComplexitySetting(): Observable<PasswordComplexityResponseModelData> {
    let url = API_BASE_URL + "api/services/app/Profile/GetPasswordComplexitySetting";
    url = url.replace(/[?&]$/, "");
    return this.http.get<PasswordComplexityResponseModelData>(url);
  }

  updatePassword(input: PasswordEditDto): Observable<void> {
    const notFoundError = new Error('Failed to update password.');

    let url = API_BASE_URL + "api/services/app/User/UpdatePassword";
    url = url.replace(/[?&]$/, "");

    return this.http.put<CreateOrUpdateUserInput>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  changePassword(input: ChangePasswordDto): Observable<void> {
    const notFoundError = new Error('Failed to change password.');

    let url = API_BASE_URL + "api/services/app/profile/changepassword";
    url = url.replace(/[?&]$/, "");

    return this.http.post<CreateOrUpdateUserInput>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  getUserPermissionsForEdit(id: number): Observable<GetUserPermissionsForEditOutput> {
    let url = API_BASE_URL + "api/services/app/User/GetUserPermissionsForEdit?";
    if (id !== undefined)
      url += "Id=" + encodeURIComponent("" + id) + "&";
    url = url.replace(/[?&]$/, "");
    return this.http.get<GetUserPermissionsForEditOutput>(url);
  }

  updateUserPermissions(input: UpdateUserPermissionsInput): Observable<void> {
    const notFoundError = new Error('Failed to update user permissions.');

    let url = API_BASE_URL + "api/services/app/User/UpdateUserPermissions";
    url = url.replace(/[?&]$/, "");

    return this.http.put<UpdateUserPermissionsInput>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  resetUserSpecificPermissions(input: EntityDtoOfInt64): Observable<void> {
    const notFoundError = new Error('Failed to reset user permissions.');

    let url = API_BASE_URL + "api/services/app/User/ResetUserSpecificPermissions";
    url = url.replace(/[?&]$/, "");

    return this.http.post<EntityDtoOfInt64>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }
  //end:: users

  //start:: roles

  getRolesPagination(filter: RolesFilterModel): Observable<IPagedRoleModelData> {
    let url = API_BASE_URL + "api/Role/GetRoles?";
    url += `skipCount=${filter.skipCount}`
    if (filter.maxResultCount) {
      url += `&maxResultCount=${filter.maxResultCount}`
    }
    if (filter.searchText) {
      url += `&searchText=${filter.searchText}`
    }
    url = url.replace(/[?&]$/, "");

    return this.http.get<IPagedRoleModelData>(url);
  }

  getRoleForEdit(id: number): Observable<GetRoleForEditOutput> {
    let parameters = new HttpParams();

    if (id != null) {
      parameters = new HttpParams()
        .set('Id', encodeURIComponent(id.toString()));
    }

    let url = API_BASE_URL + "api/services/app/Role/GetRoleForEdit";
    url = url.replace(/[?&]$/, "");

    return this.http.get<GetRoleForEditOutput>(url, { params: parameters });
  }

  getUsersForRole(id: number): Observable<UserDto> {
    let roleId = "";
    if (id != null)
      roleId = encodeURIComponent(id.toString());

    let url = API_BASE_URL + "api/role/" + roleId + " /users?roleId=" + roleId;
    url = url.replace(/[?&]$/, "");

    return this.http.get<UserDto>(url);
  }

  createOrUpdateRole(input: CreateOrUpdateRoleInput): Observable<void> {
    const notFoundError = new Error('Failed to create/update role.');

    let url = API_BASE_URL + "api/services/app/Role/CreateOrUpdateRole";
    url = url.replace(/[?&]$/, "");

    return this.http.post<CreateOrUpdateRoleInput>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  deleteRole(id: number): Observable<void> {
    const notFoundError = new Error('Failed to delete user.');

    let url = API_BASE_URL + "api/services/app/Role/DeleteRole?";
    if (id !== undefined)
      url += "Id=" + encodeURIComponent("" + id) + "&";
    url = url.replace(/[?&]$/, "");

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.http.delete<void>(url, options_).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  //end:: roles

  //start:: property groups

  getPropertyGroups(): Observable<PropertyGroupsResult> {
    let url = API_BASE_URL + "api/services/app/PropertyGroups/GetAll?MaxResultCount=250";
    url = url.replace(/[?&]$/, "");
    return this.http.get<PropertyGroupsResult>(url);
  }

  createOrEditGroup(input: CreateOrEditPropertyGroupDto): Observable<void> {
    const notFoundError = new Error('Failed to create/update group.');

    let url = API_BASE_URL + "api/services/app/PropertyGroups/CreateOrEdit";
    url = url.replace(/[?&]$/, "");

    return this.http.post<CreateOrEditPropertyGroupDto>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  deleteGroup(id: number): Observable<void> {
    const notFoundError = new Error('Failed to delete group.');

    let url = API_BASE_URL + "api/services/app/PropertyGroups/Delete?";
    if (id !== undefined)
      url += "Id=" + encodeURIComponent("" + id) + "&";
    url = url.replace(/[?&]$/, "");

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.http.delete<void>(url, options_).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  getPropertyUsers(propertyGroupId: number): Observable<PropertyGroupUserDto[]> {
    let url = API_BASE_URL + "api/services/app/PropertyGroupUsers/GetAll?";
    if (propertyGroupId !== undefined) {
      url += 'propertGroupId=' + encodeURIComponent('' + propertyGroupId) + '&';
    }
    url = url.replace(/[?&]$/, "");
    return this.http.get<PropertyGroupUserDto[]>(url);
  }

  createGroupUser(input: CreateOrEditPropertyGroupUserDto): Observable<void> {
    const notFoundError = new Error('Failed to add a group user(s).');

    let url = API_BASE_URL + "api/services/app/PropertyGroupUsers/Create";
    url = url.replace(/[?&]$/, "");

    return this.http.post<CreateOrEditPropertyGroupUserDto>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  deleteGroupUser(propertyGroupId: number, userId: number): Observable<void> {
    const notFoundError = new Error('Failed to delete user.');

    let url = API_BASE_URL + "api/services/app/PropertyGroupUsers/Delete?";
    if (propertyGroupId !== undefined)
      url += 'propertyGroupId=' + encodeURIComponent('' + propertyGroupId) + '&' + 'userId=' + encodeURIComponent('' + userId);
    url = url.replace(/[?&]$/, "");

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.http.delete<void>(url, options_).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  getPropertySites(propertyGroupId: number): Observable<TenantsPropertyDto[]> {
    let url = API_BASE_URL + "api/shared-site/sites-by-property-group?";
    if (propertyGroupId !== undefined) {
      url += 'propertyGroupId=' + encodeURIComponent('' + propertyGroupId) + '&';
    }
    url = url.replace(/[?&]$/, "");
    return this.http.get<TenantsPropertyDto[]>(url);
  }

  createGroupSites(input: CreateOrEditPropertyGroupSitesDto): Observable<void> {
    const notFoundError = new Error('Failed to add a group site(s).');

    let url = API_BASE_URL + "api/services/app/PropertyGroupSites/Create";
    url = url.replace(/[?&]$/, "");

    return this.http.post<CreateOrEditPropertyGroupSitesDto>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  deleteGroupSites(propertyGroupId: number, siteId: number): Observable<void> {
    const notFoundError = new Error('Failed to delete property group site.');

    let url = API_BASE_URL + "api/services/app/PropertyGroupSites/Delete?";
    if (propertyGroupId !== undefined)
      url += 'propertyGroupId=' + encodeURIComponent('' + propertyGroupId) + '&' + 'siteId=' + encodeURIComponent('' + siteId);
    url = url.replace(/[?&]$/, "");

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      })
    };

    return this.http.delete<void>(url, options_).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }
  //end:: property groups

  //start:: notification settings
  resetNotificationSettingsForUser(userId: number): Observable<void> {
    const notFoundError = new Error('Failed to update user notification settings.');

    let url = API_BASE_URL + "api/notification/settings/reset/user/" + userId;
    url = url.replace(/[?&]$/, "");

    return this.http.put<void>(url, userId).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    );
  }

  resetNotificationSettingsForRole(role: string): Observable<void> {
    const notFoundError = new Error('Failed to update user notification settings.');

    let url = API_BASE_URL + "api/notification/settings/reset/role/" + role;
    url = url.replace(/[?&]$/, "");

    return this.http.put<void>(url, role).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    );
  }

  enableNotificationsForSites(userSiteList: UserSiteListModel): Observable<void> {
    const notFoundError = new Error('Failed to update user notification settings.');

    let url = API_BASE_URL + "api/notification/settings/sites";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, userSiteList).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    );
  }

  disableNotificationsForSites(userSiteList: UserSiteListModel): Observable<void> {
    const notFoundError = new Error('Failed to update user notification settings.');

    let url = API_BASE_URL + "api/notification/settings/sites/delete";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, userSiteList).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    );
  }
  //end:: notification settings

  //start:: camera permissions
  createCameraPermissions(input: CreateCameraPermissionsDto): Observable<void> {
    const notFoundError = new Error('Failed to set camera permission.');

    let url = API_BASE_URL + "api/Camera/permissions";
    url = url.replace(/[?&]$/, "");

    return this.http.post<void>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  getCameraPermissions(propertyGroupId: number, siteId: number): Observable<GetCameraPermissionDto[]> {
    let propertyGroupIdString = encodeURIComponent(propertyGroupId.toString());
    let siteIdString = encodeURIComponent(siteId.toString());
    let url = API_BASE_URL + "api/Camera/permissions?propertyGroupId=" + propertyGroupIdString + "&siteId=" + siteIdString;
    url = url.replace(/[?&]$/, "");

    return this.http.get<GetCameraPermissionDto[]>(url);
  }

  grantCameraPermissions(input: GrantCameraPermissionDto[]): Observable<void> {
    const notFoundError = new Error('Failed to grant camera permission.');

    let url = API_BASE_URL + "api/camera/permissions/grant";
    url = url.replace(/[?&]$/, "");

    return this.http.put<void>(url, input).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  revokeAllCameraPermissions(propertyGroupId: number, siteId: number): Observable<void> {
    const notFoundError = new Error('Failed to revoke all camera permission.');

    let propertyGroupIdString = encodeURIComponent(propertyGroupId.toString());
    let siteIdString = encodeURIComponent(siteId.toString());
    let url = API_BASE_URL + "api/camera/permissions/revoke?propertyGroupId=" + propertyGroupIdString + "&siteId=" + siteIdString;
    url = url.replace(/[?&]$/, "");

    return this.http.put<void>(url, propertyGroupId).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }

  deleteCameraPermissions(propertyGroupId: number, siteId: number): Observable<void> {
    const notFoundError = new Error('Failed to delete camera permission.');

    let url = API_BASE_URL + "api/camera/permissions?propertyGroupId=" + propertyGroupId + "&siteId=" + siteId;
    url = url.replace(/[?&]$/, "");

    return this.http.delete<void>(url).pipe(
      map((result: any) => {
        if (!result) {
          return notFoundError;
        }
        return result;
      })
    )
  }
  //end:: camera permissions
}
